import { Account } from 'dashboard/models/account';
import { Case } from 'dashboard/models/case';
import { CaseMessage } from 'dashboard/models/case-message';
import { DocumentGovernmentId } from 'dashboard/models/document';
import { GraphQuery } from 'dashboard/models/graph-query';
import { Inquiry } from 'dashboard/models/inquiry';
import {
  ListItemBrowserFingerprint,
  ListItemCountry,
  ListItemDeviceFingerprint,
  ListItemEmailAddress,
  ListItemFace,
  ListItemField,
  ListItemGeolocation,
  ListItemGovernmentIdNumber,
  ListItemIpAddress,
  ListItemName,
  ListItemPhoneNumber,
  ListItemString,
} from 'dashboard/models/list-item';
import { ReportAddressLookup } from 'dashboard/models/report/address-lookup';
import { ReportAdverseMedia } from 'dashboard/models/report/adverse-media';
import { ReportBusinessAdverseMedia } from 'dashboard/models/report/business-adverse-media';
import { ReportBusinessAssociatedPersons } from 'dashboard/models/report/business-associated-persons';
import { ReportBusinessClassification } from 'dashboard/models/report/business-classification';
import { ReportBusinessIndustryClassification } from 'dashboard/models/report/business-industry-classification';
import { ReportBusinessLiensLookup } from 'dashboard/models/report/business-liens-lookup';
import { ReportBusinessLookup } from 'dashboard/models/report/business-lookup';
import { ReportBusinessNonprofit } from 'dashboard/models/report/business-nonprofit';
import { ReportBusinessOnlinePresence } from 'dashboard/models/report/business-online-presence';
import { ReportBusinessRegistrationsLookup } from 'dashboard/models/report/business-registrations-lookup';
import { ReportBusinessSocialMedia } from 'dashboard/models/report/business-social-media';
import { ReportBusinessWatchlist } from 'dashboard/models/report/business-watchlist';
import { ReportBusinessWebsite } from 'dashboard/models/report/business-website';
import { ReportCryptoAddressWatchlist } from 'dashboard/models/report/crypto-address-watchlist';
import { ReportCustomList } from 'dashboard/models/report/custom-list';
import { ReportEmailAddress } from 'dashboard/models/report/email-address';
import { ReportFinraBrokerCheck } from 'dashboard/models/report/finra-broker-check';
import { ReportChainalysisAddressScreening } from 'dashboard/models/report/integration/chainalysis-address-screening';
import { ReportClearbitBusinessLookup } from 'dashboard/models/report/integration/clearbit-business-lookup';
import { ReportCoinbaseCheckCryptoRisk } from 'dashboard/models/report/integration/coinbase-check-crypto-risk';
import { ReportEquifaxOneview } from 'dashboard/models/report/integration/equifax-oneview';
import { ReportKyckrBusinessLookup } from 'dashboard/models/report/integration/kyckr-business-lookup';
import { ReportMiddeskBusinessLookup } from 'dashboard/models/report/integration/middesk-business-lookup';
import { ReportMxAccount } from 'dashboard/models/report/integration/mx-account';
import { ReportSentilinkApplicationRisk } from 'dashboard/models/report/integration/sentilink-application-risk';
import { ReportSentilinkScores } from 'dashboard/models/report/integration/sentilink-scores';
import { ReportTrmWalletScreening } from 'dashboard/models/report/integration/trm-wallet-screening';
import { ReportPhoneNumber } from 'dashboard/models/report/phone-number';
import { ReportPoliticallyExposedPerson } from 'dashboard/models/report/politically-exposed-person';
import { ReportProfile } from 'dashboard/models/report/profile';
import { ReportSecActionLookup } from 'dashboard/models/report/sec-action-lookup';
import { ReportSocialMedia } from 'dashboard/models/report/social-media';
import { ReportSynthetic } from 'dashboard/models/report/synthetic';
import { ReportWatchlist } from 'dashboard/models/report/watchlist';
import { SAR } from 'dashboard/models/sar';
import { Transaction } from 'dashboard/models/transaction';
import { Verification } from 'dashboard/models/verification';
import {
  WorkflowActionRunHttpPlatformIntegrationV1Output,
  WorkflowRunStepError,
  WorkflowStepBase,
  WorkflowStepLoopForEachSettings,
  WorkflowStepLoopMapSettings,
  WorkflowStepLoopWhileSettings,
} from 'dashboard/models/workflow-version';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

export type WorkflowRunStepKnownObjects =
  | Inquiry
  | Account
  | Case
  | CaseMessage
  | GraphQuery
  | ListItemBrowserFingerprint
  | ListItemCountry
  | ListItemDeviceFingerprint
  | ListItemEmailAddress
  | ListItemFace
  | ListItemField
  | ListItemGeolocation
  | ListItemGovernmentIdNumber
  | ListItemIpAddress
  | ListItemName
  | ListItemPhoneNumber
  | ListItemString
  | ReportAddressLookup
  | ReportAdverseMedia
  | ReportBusinessAdverseMedia
  | ReportBusinessAssociatedPersons
  | ReportBusinessClassification
  | ReportBusinessIndustryClassification
  | ReportBusinessLiensLookup
  | ReportBusinessLookup
  | ReportBusinessNonprofit
  | ReportBusinessOnlinePresence
  | ReportBusinessRegistrationsLookup
  | ReportBusinessSocialMedia
  | ReportBusinessWatchlist
  | ReportBusinessWebsite
  | ReportChainalysisAddressScreening
  | ReportClearbitBusinessLookup
  | ReportCoinbaseCheckCryptoRisk
  | ReportCryptoAddressWatchlist
  | ReportCustomList
  | ReportEmailAddress
  | ReportEquifaxOneview
  | ReportFinraBrokerCheck
  | ReportKyckrBusinessLookup
  | ReportMiddeskBusinessLookup
  | ReportMxAccount
  | ReportPhoneNumber
  | ReportPoliticallyExposedPerson
  | ReportProfile
  | ReportSecActionLookup
  | ReportSocialMedia
  | ReportSentilinkApplicationRisk
  | ReportSentilinkScores
  | ReportSynthetic
  | ReportTrmWalletScreening
  | ReportWatchlist
  | SAR
  | Transaction
  | Verification
  | DocumentGovernmentId;

export type WorkflowRunVariable =
  | WorkflowRunVariablePersonaObject
  | WorkflowRunVariableLoop
  | WorkflowRunVariableOutput
  | WorkflowRunVariablePlatformIntegration
  | WorkflowRunVariableTriggerParams;

export interface WorkflowRunVariablePersonaObject {
  type: ApiModel.WorkflowRunVariablePersonaObject;
  variable: {
    'persona-object-id': string;
    'persona-object-type': string;
  };
}

export interface WorkflowRunVariableOutput {
  type: ApiModel.WorkflowRunVariableOutput;
  variable: {
    output: Record<string, unknown>;
  };
}

export interface WorkflowRunVariablePlatformIntegration {
  type: ApiModel.WorkflowRunVariablePlatformIntegration;
  variable: WorkflowActionRunHttpPlatformIntegrationV1Output;
}

export interface WorkflowRunVariableTriggerParams {
  type: ApiModel.WorkflowRunVariableTriggerParams;
  variable: {
    params: Record<string, unknown>;
  };
}

export interface WorkflowRunVariableLoop {
  type: ApiModel.WorkflowRunVariableLoop;
  variable: {
    'num-iterations': number;
    result?: unknown[];
  };
}

export enum WorkflowRunStepStatus {
  Canceled = 'canceled',
  Completed = 'completed',
  Errored = 'errored',
  Failed = 'failed',
  Initialized = 'initialized',
  InProgress = 'in_progress',
}

export type LogSeverity = 'INFO' | 'WARN' | 'ERROR';
export interface WorkflowRunStepAttributes {
  'config-step-token': string;
  status: WorkflowRunStepStatus;

  'generated-variable': WorkflowRunVariable | null;

  'known-objects-map': Record<WorkflowStepBase['token'], string>;
  // Currently known-variables property is used by evaluate_code_step only
  // Otherwise may give blank string
  'known-variables-map': Record<
    WorkflowStepBase['token'],
    {
      output:
        | ''
        | {
            logs?: [LogSeverity, string[]][] | unknown[][];
            result?: unknown;
            'request-url'?: string;
            'request-body'?: string;
            'response-status'?: unknown;
            resolved?: Record<string, unknown>;
          }
        | Record<string, any>;
    }
  >;
  'step-error'?: WorkflowRunStepError;

  'created-at': string;
  'enqueued-at'?: string;
  'last-deprioritized-at'?: string;
  'last-processed-at'?: string;
  'completed-at'?: string;
  'updated-at': string;
  'total-attempts': number;
  'rate-limited-attempts'?: string[];
}
export interface WorkflowRunStepRelationships {
  'known-objects': ApiListRelationship<
    | ApiModel.Inquiry
    | ApiModel.Account
    | ApiModel.Case
    | ApiModel.ReportAdverseMedia
    | ApiModel.ReportBusinessAdverseMedia
    | ApiModel.ReportBusinessAssociatedPersons
    | ApiModel.ReportBusinessLookup
    | ApiModel.ReportBusinessWatchlist
    | ApiModel.ReportCryptoAddressWatchlist
    | ApiModel.ReportCustomList
    | ApiModel.ReportEmailAddress
    | ApiModel.ReportFinraBrokerCheck
    | ApiModel.ReportPhoneNumber
    | ApiModel.ReportProfile
    | ApiModel.ReportSecActionLookup
    | ApiModel.ReportAddressLookup
    | ApiModel.ReportSocialMedia
    | ApiModel.ReportSynthetic
    | ApiModel.ReportWatchlist
    | ApiModel.Transaction
  >;
  'workflow-run': ApiRelationship<ApiModel.WorkflowRun>;
}

export interface WorkflowRunStepActionAttributes extends WorkflowRunStepAttributes {}
export interface WorkflowRunStepConditionalAttributes extends WorkflowRunStepAttributes {
  'chosen-conditional-index': number;
  'chosen-or-indices': Array<number>;
  'conditionals-json'?: string;
}
export interface WorkflowRunStepLoopAttributes extends WorkflowRunStepAttributes {
  'errored-cycles': number[];
  'current-cycle': number;
  'cycle-to-step-tokens': Record<number, string[]>;
  settings:
    | WorkflowStepLoopForEachSettings
    | WorkflowStepLoopWhileSettings
    | WorkflowStepLoopMapSettings;
  'generated-variable': WorkflowRunVariableLoop | null;
  'resolved-input-array': Array<any>;
}
export interface WorkflowRunStepModuleAttributes extends WorkflowRunStepAttributes {}
export interface WorkflowRunStepParallelAttributes extends WorkflowRunStepAttributes {}
export interface WorkflowRunStepWaitAttributes extends WorkflowRunStepAttributes {}

export type WorkflowRunStepAction = ApiResource<
  WorkflowRunStepActionAttributes,
  WorkflowRunStepRelationships,
  ApiModel.WorkflowRunStepAction
>;
export type WorkflowRunStepConditional = ApiResource<
  WorkflowRunStepConditionalAttributes,
  WorkflowRunStepRelationships,
  ApiModel.WorkflowRunStepConditional
>;
export type WorkflowRunStepLoop = ApiResource<
  WorkflowRunStepLoopAttributes,
  WorkflowRunStepRelationships,
  ApiModel.WorkflowRunStepLoop
>;
export type WorkflowRunStepModule = ApiResource<
  WorkflowRunStepModuleAttributes,
  WorkflowRunStepRelationships,
  ApiModel.WorkflowRunStepModule
>;
export type WorkflowRunStepParallel = ApiResource<
  WorkflowRunStepParallelAttributes,
  WorkflowRunStepRelationships,
  ApiModel.WorkflowRunStepParallel
>;
export type WorkflowRunStepWait = ApiResource<
  WorkflowRunStepWaitAttributes,
  WorkflowRunStepRelationships,
  ApiModel.WorkflowRunStepWait
>;

export type WorkflowRunStepType =
  | ApiModel.WorkflowRunStepAction
  | ApiModel.WorkflowRunStepConditional
  | ApiModel.WorkflowRunStepLoop
  | ApiModel.WorkflowRunStepModule
  | ApiModel.WorkflowRunStepParallel
  | ApiModel.WorkflowRunStepWait;

export type WorkflowRunStep = ApiResource<
  WorkflowRunStepAttributes,
  WorkflowRunStepRelationships,
  WorkflowRunStepType
>;
